export const SERVICES = [
  {
    slug: 'lead-qualification',
    stage: 'qualify',
    stageLabel: 'Qualify',
    name: 'Lead Qualification',
    short: 'Decide which enquiries deserve your time before they reach your diary.',
    metaTitle: 'Lead Qualification Services | HyenaMind',
    metaDescription:
      'Every enquiry checked for service, area and genuine intent, then routed with the context behind the decision, so your time goes to the work worth quoting.',
    heroTitle: 'Not every enquiry is a job.',
    heroText:
      'An enquiry is not an opportunity until someone has checked that it is the work you do, in a place you cover, for a person who intends to proceed. Done well, qualification protects the scarcest thing a local business has, which is the hours available to quote.',
    visual: 'lead-qualification',
    image: '/images/trades-1.jpg',
    imageAlt: 'Electrician in a hard hat and work gloves fitting an electrical box on an exterior wall',
    problem: {
      title: 'Unqualified enquiries cost more than they look',
      text: 'The visible cost is the site visit. The hidden cost is the enquiry you answered late because an unsuitable one came first. [How people decide who to contact](/resources/contractors/how-local-customers-find-a-contractor).',
      points: [
        'Visits booked for work outside your service area',
        'Quotes produced for people who were only price-checking',
        'Good enquiries answered late because poor ones came first',
        'No record of why an enquiry was accepted or declined',
      ],
    },
    deliverables: [
      'Qualification criteria agreed with you',
      'Every enquiry checked against them',
      'Service, area and intent confirmed',
      'Project context captured',
      'Routing to the right person',
      'Declines handled politely',
      'The reason for each decision recorded',
      'Criteria reviewed as your capacity changes',
    ],
    process: [
      { title: 'Define the work you want', text: 'Trade, job size, service area and anything you will not take on. Your criteria, not a generic template.' },
      { title: 'Check every enquiry', text: 'Each one is assessed against those criteria before it reaches your diary.' },
      { title: 'Capture the context', text: 'What the job is, where it is and when they want it, so a quote can be prepared without a second conversation.' },
      { title: 'Route or decline', text: 'Qualified enquiries go to whoever should handle them. The rest get a courteous reply rather than silence.' },
      { title: 'Review the criteria', text: 'What qualifies changes as your capacity and the work you want change. The rules are revisited, not set once.' },
    ],
    faqs: [
      {
        q: 'How is this different from lead generation?',
        a: 'Lead generation creates enquiries. Qualification decides which of them deserve your time. They are often bought together, but a business already receiving plenty of enquiries may only need the second.',
      },
      {
        q: 'Is this the same as buying qualified leads from you?',
        a: 'No, and the direction is opposite. Our qualified lead service supplies opportunities to you. Qualification works on the enquiries your own marketing already produces, so they stay entirely yours.',
      },
      {
        q: 'Will I lose work if enquiries are filtered?',
        a: 'That is the risk worth naming. It is managed by agreeing the criteria with you and recording the reason for every decline, so you can see what was turned away and adjust. Nothing is filtered on a rule you have not approved.',
      },
    ],
    related: ['lead-generation', 'appointment-setting', 'crm-automation'],
    cta: { label: 'Set My Qualification Criteria', to: '/contact?intent=contractor_growth&service=lead-qualification' },
  },
  {
    slug: 'conversion-optimization',
    stage: 'convert',
    stageLabel: 'Convert',
    name: 'Conversion Rate Optimisation',
    short: 'Turn more of the visitors you already pay for into enquiries you can act on.',
    metaTitle: 'Conversion Rate Optimisation | HyenaMind',
    metaDescription:
      'Improve how many visitors become enquiries: page structure, offer, form friction, mobile behaviour, call tracking and measurement on the traffic you already have.',
    heroTitle: 'You already paid for the visit. Earn the enquiry.',
    heroText:
      'Most businesses try to fix a weak enquiry rate by buying more traffic. That multiplies the cost of the same leak. Conversion work starts with the visitors already arriving and asks a narrower question: what stopped the ones who left?',
    visual: 'cro',
    image: '/images/strategy-1.jpg',
    imageAlt: 'Two people working through a page layout sketched on paper beside laptops',
    problem: {
      title: 'More traffic is the expensive way to fix a conversion problem',
      text: 'If a page converts poorly, every extra pound or dollar of spend inherits that weakness. Fixing the page changes the return on every visit that follows, including the ones you already bought. [Where the enquiries actually go missing](/resources/contractors/how-local-customers-find-a-contractor).',
      points: [
        'Visitors arrive and leave without making contact',
        'The service and service area are not obvious in the first screen',
        'The form asks for more than a first conversation needs',
        'No one can say which pages produce enquiries and which do not',
      ],
    },
    deliverables: [
      'Conversion review of your key pages',
      'Message and offer clarity',
      'Page structure and hierarchy',
      'Form length and friction reduction',
      'Mobile and tap-target behaviour',
      'Call and enquiry tracking',
      'Event and conversion measurement',
      'A prioritised list of what to change first',
    ],
    process: [
      { title: 'Measure what happens now', text: 'Establish the current enquiry rate per page so any change can be judged against something real.' },
      { title: 'Find the drop-off', text: 'Identify where visitors leave: the first screen, the proof, the form, or the step after it.' },
      { title: 'Change the highest-cost thing first', text: 'Work in order of what is losing the most enquiries, not what is easiest to edit.' },
      { title: 'Reduce the ask', text: 'Collect what a first conversation needs and gather the rest once someone is engaged.' },
      { title: 'Keep measuring', text: 'Changes are kept or reversed on evidence from your own traffic rather than general best practice.' },
    ],
    faqs: [
      {
        q: 'How much can conversion optimisation improve my enquiries?',
        a: 'That depends entirely on where you are starting and what is currently breaking, and any specific figure quoted before looking at your pages would be invented. The honest answer is that the work is measured against your own baseline, so you can see whether it paid for itself.',
      },
      {
        q: 'Do I need to rebuild my website?',
        a: 'Usually not. A great deal of conversion work is structural rather than visual: what the first screen says, how much the form asks for, whether the phone number is tappable, and whether anything is being measured at all.',
      },
      {
        q: 'Is this only useful if I run ads?',
        a: 'No. It applies to any traffic you already have, paid or organic. It tends to matter most to businesses running ads, simply because every lost visitor there has a direct cost attached.',
      },
    ],
    related: ['web-design-development', 'tracking-analytics', 'google-ads'],
    cta: { label: 'Review My Conversion Path', to: '/contact?intent=contractor_growth&service=conversion-optimization' },
  },
  {
    slug: 'appointment-setting',
    stage: 'follow-up',
    stageLabel: 'Follow up',
    name: 'Appointment Setting & Follow-Up',
    short: 'Reply first, book the visit and make sure no enquiry is lost to silence.',
    metaTitle: 'Appointment Setting & Follow-Up | HyenaMind',
    metaDescription:
      'Fast acknowledgement, qualification, booked appointments, confirmations and reminders, so enquiries become visits instead of being lost to a delayed reply.',
    heroTitle: 'Most enquiries are lost to silence, not to competitors.',
    heroText:
      'People contact several businesses and compare the replies as they arrive. The first useful response usually sets the terms the rest are judged against. The problem is that the people best placed to reply are on site with their hands full.',
    visual: 'appointment-setting',
    image: '/images/service-van-driveway.jpg',
    imageAlt: 'Worker in plain coveralls stepping out of an unmarked white van parked on a residential driveway',
    problem: {
      title: 'The gap between an enquiry and a reply is where work disappears',
      text: 'An enquiry that waits until the evening is competing with one that was answered at midday. Nothing about the quality of your work enters that comparison.',
      points: [
        'Enquiries arrive while you are on a job',
        'Calls are missed and not returned the same day',
        'Appointments are booked and then forgotten by the customer',
        'No record of which enquiries were followed up and which were not',
      ],
    },
    deliverables: [
      'Immediate acknowledgement of every enquiry',
      'Qualification before anything reaches your diary',
      'Appointment booking into your availability',
      'Confirmation messages',
      'Reminders before the visit',
      'Follow-up when there is no answer',
      'Reschedule handling',
      'A record of every enquiry through to outcome',
    ],
    process: [
      { title: 'Define what is worth booking', text: 'Service, area and job type, so your diary fills with visits you actually want.' },
      { title: 'Acknowledge immediately', text: 'Every enquiry gets a reply straight away, even when you cannot answer personally.' },
      { title: 'Qualify before the diary', text: 'Enquiries are checked against your criteria so unsuitable ones do not consume a visit.' },
      { title: 'Book and confirm', text: 'A time is agreed and confirmed in writing, with a reminder before the visit.' },
      { title: 'Chase the silence', text: 'Enquiries that go quiet are followed up on a schedule rather than when someone remembers.' },
    ],
    faqs: [
      {
        q: 'Will someone answer the phone as my business?',
        a: 'How enquiries are handled is agreed with you before anything goes live, including what is said, what is promised and what is escalated to you. Nothing is set up that commits you to work you have not agreed to.',
      },
      {
        q: 'How quickly are enquiries answered?',
        a: 'A response window is agreed with you and then actually measured, rather than described in general terms. We will not quote an industry figure here, because the only number that matters is the one your enquiries get.',
      },
      {
        q: 'Does this replace my own follow-up?',
        a: 'It is usually a safety net rather than a replacement. The aim is that nothing goes unanswered when you are on site, not that you stop speaking to your own customers.',
      },
    ],
    related: ['lead-qualification', 'crm-automation', 'lead-generation'],
    cta: { label: 'Set Up Appointment Follow-Up', to: '/contact?intent=contractor_growth&service=appointment-setting' },
  },
  {
    slug: 'crm-automation',
    stage: 'grow',
    stageLabel: 'Grow',
    name: 'CRM & Marketing Automation',
    short: 'One place every enquiry lives, with the routine follow-up running on its own.',
    metaTitle: 'CRM & Marketing Automation | HyenaMind',
    metaDescription:
      'Put every enquiry in one place, route it to the right person, automate routine follow-up, and say which enquiries became work and where they came from.',
    heroTitle: 'If you cannot see the pipeline, you cannot improve it.',
    heroText:
      'Enquiries arriving across a phone, an inbox, a form and a social account are four separate memories, not a pipeline. A CRM is simply the decision to keep them in one place, and automation is the decision to stop doing the repetitive parts by hand.',
    visual: 'crm-automation',
    image: '/images/local-service-area-aerial.jpg',
    imageAlt: 'Aerial view of a residential neighbourhood of streets, driveways and rooftops',
    problem: {
      title: 'Enquiries in four places are not a pipeline',
      text: 'The practical cost is not untidiness. It is that nobody can answer a simple question: of the enquiries that arrived last month, how many became work, and where did they come from?',
      points: [
        'Enquiries spread across phone, inbox, forms and social',
        'Follow-up depends on who remembers',
        'No reliable view of what is open and what went quiet',
        'Marketing spend cannot be judged against actual jobs won',
      ],
    },
    deliverables: [
      'CRM set up around how you actually work',
      'Every enquiry captured in one place',
      'Routing and ownership rules',
      'Automated follow-up sequences',
      'Appointment and reminder automation',
      'Source and campaign attribution',
      'Pipeline stages from enquiry to outcome',
      'Reporting you can act on',
    ],
    process: [
      { title: 'Map how work really arrives', text: 'Every route an enquiry currently takes, including the informal ones, before anything is automated.' },
      { title: 'Choose the smallest system that fits', text: 'A CRM you will actually use beats a larger one nobody opens. We will not sell complexity you do not need.' },
      { title: 'Connect the sources', text: 'Forms, calls and campaigns feed one record, so attribution survives past the first click.' },
      { title: 'Automate the repetitive parts', text: 'Acknowledgements, reminders and follow-up sequences run without anyone remembering to send them.' },
      { title: 'Report on outcomes, not activity', text: 'Jobs won against source, so spending decisions rest on work rather than on clicks.' },
    ],
    faqs: [
      {
        q: 'Do I have to change the CRM I already use?',
        a: 'Not necessarily. If what you have works and your team uses it, the better job is usually connecting it properly and automating what is still manual. Replacing a familiar system has a real cost in adoption.',
      },
      {
        q: 'Is this only worth it for larger businesses?',
        a: 'The threshold is lower than most expect. It is worth it as soon as enquiries arrive faster than one person can reliably remember them, which for many businesses is well before they feel large.',
      },
      {
        q: 'Do you set up GoHighLevel?',
        a: 'Yes. GoHighLevel is one of the platforms we implement and automate, and we also connect CRMs a business already uses. Which one fits depends on how your team works and what it already relies on.',
      },
      {
        q: 'What about AI tools?',
        a: 'Used where they genuinely help, such as drafting replies or sorting enquiries, and avoided where they would put an unreviewed message in front of a customer. The test is whether it saves real time without risking the relationship.',
      },
    ],
    related: ['appointment-setting', 'tracking-analytics', 'lead-qualification'],
    cta: { label: 'Organise My Pipeline', to: '/contact?intent=contractor_growth&service=crm-automation' },
  },
  {
    slug: 'web-design-development',
    stage: 'build',
    stageLabel: 'Build',
    name: 'Web Design & Development',
    short: 'Websites and landing pages built to be found, trusted and acted on.',
    metaTitle: 'Web Design & Development for Local Businesses | HyenaMind',
    metaDescription:
      'Strategy, UX, design and development for service businesses: fast, accessible websites and landing pages with SEO foundations and conversion tracking built in.',
    heroTitle: 'A website built to win the enquiry, not the compliment.',
    heroText:
      'Your website is where every ad click, search result and referral lands. For a service business it has one job above the others: make it obvious what you do, where you do it and why you, then make contacting you easy. We plan, design, build and measure it as one piece of work.',
    visual: 'web-design',
    problem: {
      title: 'Most service-business websites leak enquiries',
      text: 'The usual causes are ordinary and fixable: a first screen that does not say what the business does or where, pages that load slowly on the phones most visitors use, and no measurement, so nobody can say which pages produce enquiries. We break down where the leak usually starts in [why contractor websites stop turning traffic into calls](/resources/contractors/why-contractors-arent-getting-local-leads).',
      points: [
        'The service and the area covered are unclear in the first screen',
        'Slow on the phones most visitors use',
        'One page trying to rank and convert for every service',
        'No tracking, so nobody knows which pages produce enquiries',
      ],
    },
    deliverables: [
      'Strategy, sitemap and page architecture',
      'UX and conversion-focused design',
      'Development: React, custom builds or a CMS that fits',
      'Service pages and campaign landing pages',
      'Mobile-first performance and Core Web Vitals',
      'Accessible build, checked against WCAG 2.2 AA',
      'Technical SEO foundations and structured data',
      'Analytics, call and form tracking from launch',
    ],
    fit: {
      text: 'Businesses whose website is a lead source, or should be.',
      points: [
        'Your site no longer reflects the work you do or the areas you cover',
        'Visitors arrive but few of them call or fill in a form',
        'Ads are sent to a homepage rather than a page built for the offer',
        'The site runs on an older platform that is slow or hard to update: see [PHP migration and modernization](/php-migration)',
      ],
    },
    process: [
      { title: 'Strategy', text: 'Services, areas, ideal customers and the action each page should earn, agreed before anything is designed.' },
      { title: 'Structure', text: 'A sitemap with one page per service and intent, so each can rank and convert on its own.' },
      { title: 'Design', text: 'Layouts built around how your customers decide: what you do, proof that you do it well, and an easy next step.' },
      { title: 'Development', text: 'A fast, accessible, mobile-first build with clean SEO foundations and tracking wired in from day one.' },
      { title: 'Launch and measure', text: 'Redirects for any old URLs, Search Console checks after launch, and enquiry data that shows what to improve next.' },
    ],
    costFactors: [
      { t: 'Pages and templates', d: 'A focused five-page site and a forty-page service-and-area build are different projects. Repeated templates cost less than unique layouts.' },
      { t: 'Content readiness', d: 'Copy, photographs and approvals usually set the schedule more than development does.' },
      { t: 'Integrations', d: 'Booking, CRM, payments, quoting tools or a headless CMS each add build and testing time.' },
      { t: 'Replacing an existing site', d: 'Keeping URLs, redirects and search visibility intact through a rebuild is planned work, not an afterthought.' },
    ],
    measures: [
      { t: 'Enquiry rate by page', d: 'Forms, connected calls and bookings per visit, so the pages that convert can be told from the ones that do not.' },
      { t: 'Core Web Vitals', d: 'Loading, responsiveness and layout stability measured for real visitors, not only a desktop test score.' },
      { t: 'Search visibility of key pages', d: 'Indexing, impressions and clicks for each service page in Search Console.' },
    ],
    faqs: [
      { q: 'How long does a new website take?', a: 'It depends on the number of pages, how ready the content is and which integrations are needed, more than on design. You get a written schedule after the strategy stage, and development starts once the structure is agreed.' },
      { q: 'Do you build on WordPress, React or something else?', a: 'Whatever fits how the site will be used and maintained. A marketing site your team edits every week needs a different setup from a fast custom front end or a web application. We recommend a platform after the strategy stage and explain the trade-offs.' },
      { q: 'Can you redesign my site without losing search traffic?', a: 'The risk is managed rather than ignored: every existing URL is mapped to its new equivalent with a permanent redirect, content that ranks is kept or improved, and Search Console is checked after launch for errors and dropped pages.' },
      { q: 'Will the site be ready for Google Ads and SEO?', a: 'Yes. Every build includes the technical SEO foundations, page speed, landing-page structure and conversion tracking that paid and organic campaigns depend on.' },
    ],
    related: ['seo-local-seo', 'conversion-optimization', 'tracking-analytics'],
    contractor: [{ to: '/contractors/web-design', label: 'Web design for contractors' }],
    cta: { label: 'Plan My Website', to: '/contact?intent=contractor_growth&service=web-design-development' },
  },
  {
    slug: 'seo-local-seo',
    stage: 'get-found',
    stageLabel: 'Get found',
    name: 'SEO & Local SEO',
    short: 'Technical, on-page and local SEO that puts you in front of people searching nearby.',
    metaTitle: 'SEO & Local SEO Services for Local Businesses | HyenaMind',
    metaDescription:
      'Technical SEO, Google Business Profile, service pages, reviews and content for local and service businesses. Measured in enquiries, with no ranking guarantees.',
    heroTitle: 'Be the business people find when they search nearby.',
    heroText:
      'Most local buying starts with a search: for a service, a problem, or something "near me". SEO earns the organic results; local SEO earns the map results and a Business Profile people act on. We work on both, starting with whatever is stopping search engines from understanding and trusting the site you already have.',
    visual: 'seo',
    problem: {
      title: 'If search engines cannot read it, nobody finds it',
      text: 'Visibility is lost to ordinary, fixable problems more often than to competitors: pages that cannot be crawled, one page trying to rank for every service, a Business Profile left half finished. Our guide to [why contractors are not getting enough local leads from Google](/resources/contractors/why-contractors-arent-getting-local-leads) walks through the usual causes, and they apply well beyond the trades.',
      points: [
        'Missing from the local map results',
        'Service pages that do not exist, or say too little to rank',
        'Technical problems that block crawling or indexing',
        'Traffic that never turns into calls or forms',
      ],
    },
    deliverables: [
      'Technical SEO audit and fixes',
      'Search Console and Bing Webmaster Tools set up',
      'Google Business Profile optimisation',
      'Service and location page strategy',
      'On-page optimisation and internal linking',
      'Structured data that matches what the page shows',
      'A repeatable process for earning reviews',
      'Content that answers the questions customers ask',
    ],
    fit: {
      text: 'Businesses whose customers search before they buy.',
      points: [
        'Customers search for your service within a defined area',
        'You depend on referrals or ads and want a source that keeps working when spend stops',
        'Your site was redesigned or migrated and visibility dropped',
        'Your Business Profile exists but rarely produces calls',
      ],
    },
    process: [
      { title: 'Audit', text: 'Crawling, indexing, page speed, content gaps, Business Profile and the competitors ahead of you. An honest baseline from Search Console data.' },
      { title: 'Foundations', text: 'Fix what stops search engines reading and trusting the site before producing anything new.' },
      { title: 'Pages that match intent', text: 'One strong page per service and genuine location, written to answer what people search for.' },
      { title: 'Local presence', text: 'Business Profile categories, services, photos and reviews, kept consistent with the website.' },
      { title: 'Measure and extend', text: 'Impressions, clicks and enquiries reviewed monthly, then the next most valuable gap.' },
    ],
    costFactors: [
      { t: 'Size and state of the site', d: 'A site with crawling or indexing problems needs repair before new content will count.' },
      { t: 'Services and locations', d: 'Each service and each genuine service area needs its own useful page, not a template with the town name swapped.' },
      { t: 'Competition in your market', d: 'Some searches are contested by large, established sites. We say which are realistic before you commit.' },
      { t: 'What you can supply', d: 'Real project photos, reviews and expertise make pages better than anything written from the outside.' },
    ],
    measures: [
      { t: 'Search Console', d: 'Impressions, clicks and indexed pages, split between searches for your brand and searches for your services.' },
      { t: 'Business Profile actions', d: 'Calls, website visits and direction requests from your profile.' },
      { t: 'Enquiries from organic search', d: 'Forms and connected calls attributed to organic and local search in analytics.' },
    ],
    faqs: [
      { q: 'Can you guarantee first place on Google?', a: 'No. Google itself states that no one can guarantee a #1 ranking on Google, and advises caution with anyone who promises one. We commit to the work, to explaining it, and to measuring whether it produces enquiries.' },
      { q: 'How long does SEO take to work?', a: 'Technical fixes and Business Profile improvements can show once search engines recrawl the pages; rankings for competitive services, built on content and reputation, usually take months. We set expectations from your own Search Console baseline rather than a general promise.' },
      { q: 'What is the difference between SEO and local SEO?', a: 'SEO earns positions in the organic results. Local SEO is the part that affects the map results and your Business Profile for searches with local intent, which Google says are mainly based on relevance, distance and popularity.' },
      { q: 'Does SEO still matter now that search shows AI answers?', a: 'Yes. Google says a page must be indexed and eligible to show a snippet to appear in its AI features, and that no special file or markup is required. The fundamentals carry over: crawlable pages, clear answers and real expertise.' },
    ],
    related: ['web-design-development', 'google-ads', 'conversion-optimization'],
    contractor: [
      { to: '/contractors/local-seo', label: 'Local SEO for contractors' },
      { to: '/contractors/seo', label: 'SEO for contractors' },
    ],
    cta: { label: 'Get My SEO Review', to: '/contact?intent=contractor_growth&service=seo-local-seo' },
  },
  {
    slug: 'google-ads',
    stage: 'attract',
    stageLabel: 'Attract',
    name: 'Google Ads',
    short: 'Search campaigns that capture high-intent local demand and convert it into enquiries.',
    metaTitle: 'Google Ads Management for Local Businesses | HyenaMind',
    metaDescription:
      'Google Ads management for local and service businesses: search campaign structure, negative keywords, matched landing pages and tracking tied to real enquiries.',
    heroTitle: 'Be there when someone searches for exactly what you do.',
    heroText:
      'Search ads reach people at the moment they look for a service, which makes them the fastest channel for many local businesses and the easiest one to waste money on. We build and manage Google Ads around tight intent, honest tracking and the quality of the enquiries they produce.',
    visual: 'google-ads',
    problem: {
      title: 'Google Ads loses money quietly when it is run loosely',
      text: 'Broad keywords, missing negatives, a homepage as the landing page and conversion tracking that counts the wrong things turn ad spend into tuition. Discipline is the difference, and more budget rarely substitutes for it: [here is what usually does](/resources/contractors/why-contractors-arent-getting-local-leads).',
      points: [
        'Paying for searches that were never going to buy',
        'Broad match and automated settings left unchecked',
        'Ads sent to a homepage instead of a matching page',
        'Conversions counted that are not real enquiries',
      ],
    },
    deliverables: [
      'Account and campaign structure',
      'Keyword research and match-type strategy',
      'Negative keyword management',
      'Ad copy and assets',
      'Landing pages matched to each ad group',
      'Conversion tracking for calls, forms and bookings',
      'Offline conversion import from your CRM, where it fits',
      'Search-terms review and ongoing optimisation',
    ],
    fit: {
      text: 'Businesses that sell something people search for when they need it.',
      points: [
        'People search for your service when the need arises',
        'You can answer calls and enquiries quickly',
        'You want enquiries this month while organic visibility builds',
        'You have spent on Google Ads before and could not tell what worked',
      ],
    },
    process: [
      { title: 'Account architecture', text: 'Campaigns structured around your services, service areas and the value of each type of job.' },
      { title: 'Keywords and negatives', text: 'Target the searches that signal a need. Exclude, deliberately and continuously, the ones that never become work.' },
      { title: 'Ads and landing pages', text: 'Each ad group sends people to a page that answers the exact search they made.' },
      { title: 'Tracking', text: 'Real conversions only: submitted enquiries, connected calls and bookings, imported from your CRM where possible.' },
      { title: 'Optimise for quality', text: 'Bids, search terms, ads and pages refined toward qualified enquiries rather than clicks.' },
    ],
    costFactors: [
      { t: 'Your market', d: 'Click prices are set in an auction and vary widely by service and area. The budget follows from them, not the other way round.' },
      { t: 'Services and areas', d: 'Each distinct service or region usually needs its own campaign or ad group, keywords and landing page.' },
      { t: 'Landing pages', d: 'Pages that already match each offer reduce set-up time; missing ones have to be built first.' },
      { t: 'Existing tracking', d: 'If conversions are not measured reliably, that is fixed before spend is increased.' },
    ],
    measures: [
      { t: 'Cost per qualified enquiry', d: 'What a usable enquiry costs, not what a click costs.' },
      { t: 'Conversion actions that are real', d: 'Calls over a sensible duration, submitted forms and booked appointments, with duplicates removed.' },
      { t: 'Search terms', d: 'The actual searches that triggered your ads, reviewed so budget only goes where intent is right.' },
    ],
    faqs: [
      { q: 'How much should I spend on Google Ads?', a: 'It depends on what clicks cost for your services in your area and how many enquiries you can handle. We estimate that from Google\'s own keyword data before recommending a starting budget, and do not recommend scaling spend until tracking shows what a qualified enquiry costs.' },
      { q: 'How fast will I see enquiries?', a: 'Search ads can produce enquiries soon after campaigns, landing pages and tracking go live. Quality improves over the following weeks as search terms are reviewed and the campaigns learn from real conversions.' },
      { q: 'Should I also use Local Services Ads?', a: 'Where your business category and country are eligible, Local Services Ads appear above standard search ads and charge per lead rather than per click. They complement search campaigns rather than replace them, and we check eligibility at the start.' },
      { q: 'Who owns the ad account?', a: 'You do. The account, the data and the conversion history stay in your name, so nothing is lost if you ever change who manages it.' },
    ],
    related: ['conversion-optimization', 'tracking-analytics', 'meta-ads'],
    contractor: [{ to: '/contractors/google-ads', label: 'Google Ads for contractors' }],
    cta: { label: 'Review My Google Ads', to: '/contact?intent=contractor_growth&service=google-ads' },
  },
  {
    slug: 'meta-ads',
    stage: 'attract',
    stageLabel: 'Attract',
    name: 'Meta Ads',
    short: 'Facebook and Instagram campaigns that create demand before people start searching.',
    metaTitle: 'Meta Ads Management for Local Businesses | HyenaMind',
    metaDescription:
      'Facebook and Instagram advertising for local and service businesses: creative, audiences, retargeting, lead forms or landing pages, and Conversions API tracking.',
    heroTitle: 'Reach people before they start searching.',
    heroText:
      'Facebook and Instagram put your work in front of people in your area who are not looking for you yet, and bring back the ones who visited and left. That makes Meta a demand-creation channel, which is why the creative, the audience and the follow-up path matter more than the budget.',
    visual: 'meta-ads',
    problem: {
      title: 'Boosted posts are not a strategy',
      text: 'Random boosts with no audience logic, no retargeting and no qualification produce likes rather than customers. Paid social works best inside a pipeline you own, which is the same argument as [moving away from shared lead marketplaces](/resources/contractors/stop-buying-shared-contractor-leads).',
      points: [
        'Creative that looks like every other ad in the feed',
        'Instant forms collecting details from people with no real intent',
        'No retargeting of people who already visited',
        'Browser-only tracking that misses conversions',
      ],
    },
    deliverables: [
      'Offer and creative strategy',
      'Creative direction using your real work',
      'Audience and placement strategy',
      'Prospecting and retargeting campaigns',
      'Lead forms or landing pages, chosen per offer',
      'Meta Pixel and Conversions API set up',
      'Qualifying questions on every lead form',
      'Creative testing and ongoing optimisation',
    ],
    fit: {
      text: 'Businesses whose work can be shown, not only described.',
      points: [
        'Your work photographs or films well',
        'You can follow up on new enquiries quickly',
        'You want demand beyond the people already searching',
        'You run search ads and want to bring back visitors who left',
      ],
    },
    process: [
      { title: 'Offer and audience', text: 'What you are offering, to whom, and within what area, before any creative is made.' },
      { title: 'Creative', text: 'Real work, real places and clear offers, in formats made for the feed and for short video.' },
      { title: 'Campaigns', text: 'Prospecting to reach new people and retargeting to bring back the ones who showed interest.' },
      { title: 'Lead flow', text: 'A form or landing page that captures the context a first conversation needs, then qualifies it.' },
      { title: 'Optimise', text: 'Creative and audiences tested and refined toward qualified enquiries rather than cheap form fills.' },
    ],
    costFactors: [
      { t: 'Creative production', d: 'Using photos and video you already have is quicker than organising new shoots.' },
      { t: 'Audience and market', d: 'Small service areas limit how many people can be reached and how fast campaigns learn.' },
      { t: 'Lead form or landing page', d: 'A landing page may need building; an instant form needs careful qualifying questions.' },
      { t: 'Tracking', d: 'Conversions API set-up depends on your website platform and CRM.' },
    ],
    measures: [
      { t: 'Cost per qualified enquiry', d: 'Cheap leads that never answer the phone are not cheap. Quality is measured, not assumed.' },
      { t: 'Contact rate', d: 'How many people who submitted a form could actually be reached, by campaign and creative.' },
      { t: 'Reliable conversion data', d: 'Events recorded through both the Pixel and the Conversions API, so the campaigns optimise on complete data.' },
    ],
    faqs: [
      { q: 'Instant forms or a landing page?', a: 'Instant forms keep people inside Facebook or Instagram, so more of them submit, often with less intent. A landing page asks more of the visitor and usually produces fewer, better enquiries. We choose per offer and add qualifying questions whenever a form is used.' },
      { q: 'Is Meta better than Google Ads?', a: 'They do different jobs. Google Ads captures people already searching; Meta reaches people before they search and brings back visitors who left. Many local businesses benefit from both, which is why they sit in the same system.' },
      { q: 'Do you run TikTok or LinkedIn ads as well?', a: 'Where the audience is genuinely there. TikTok suits visual work aimed at consumers; LinkedIn suits businesses that sell to other businesses. We recommend a platform only when it fits who you need to reach.' },
      { q: 'Who makes the ad creative?', a: 'We direct the creative strategy and production, using your real project photos and video wherever possible, because authentic work outperforms stock imagery.' },
    ],
    related: ['social-media-marketing', 'google-ads', 'lead-qualification'],
    contractor: [{ to: '/contractors/meta-ads', label: 'Meta Ads for contractors' }],
    cta: { label: 'Plan My Meta Ads', to: '/contact?intent=contractor_growth&service=meta-ads' },
  },
  {
    slug: 'social-media-marketing',
    stage: 'attract',
    stageLabel: 'Attract',
    name: 'Social Media Marketing',
    short: 'Strategy, content and presence that turn your work into trust and demand.',
    metaTitle: 'Social Media Marketing for Local Businesses | HyenaMind',
    metaDescription:
      'Social strategy, content systems, publishing and paid-social integration for local and service businesses, built to turn real work into trust and enquiries.',
    heroTitle: 'Your work is the content. We make it perform.',
    heroText:
      'People check a business\'s social profiles before they call, even when they found it somewhere else. A profile showing recent, real work answers two questions at once: are they good, and are they still active? We build the content system that keeps it current and connects it to your enquiry path.',
    visual: 'tiktok-ads',
    problem: {
      title: 'Inconsistent posting builds nothing',
      text: 'A dormant profile suggests a dormant business. A steady, specific presence makes you the obvious choice before anyone asks for a quote. Presence only pays off when it feeds a system you control, as covered in [building a lead generation system you own](/resources/contractors/stop-buying-shared-contractor-leads).',
      points: ['Good work nobody sees', 'No content system or calendar', 'Presence disconnected from enquiries'],
    },
    deliverables: ['Social strategy', 'Content calendar and publishing', 'Creative direction', 'A system for capturing work as it happens', 'Engagement and community management', 'Paid social integration', 'Profile and brand presence', 'Links into your enquiry path'],
    fit: {
      text: 'Businesses with work worth showing and customers who look before they call.',
      points: [
        'Customers check your profiles before contacting you',
        'Your work changes visibly from start to finish',
        'Your profiles have gone quiet',
        'You plan to run paid social and need a presence behind it',
      ],
    },
    process: [
      { title: 'Strategy', text: 'Platforms, positioning and content pillars chosen for your audience, not for trends.' },
      { title: 'Content system', text: 'A repeatable way to capture work as it happens: before, during and after.' },
      { title: 'Publish and engage', text: 'Consistent publishing and replies that build trust over time.' },
      { title: 'Integrate', text: 'Social connected to paid campaigns and your enquiry path, so presence has a purpose.' },
    ],
    faqs: [
      { q: 'Which platforms should a local business be on?', a: 'The ones your customers use. Instagram and Facebook suit most visual local services, TikTok reaches a discovery-driven audience, and LinkedIn matters for services sold to other businesses. We recommend platforms from where your customers actually are.' },
      { q: 'Do I need to be on camera?', a: 'No. The work, the craft and the before-and-after carry the content. You can appear if you want to, but it is not required.' },
      { q: 'Can social media generate leads?', a: 'Yes, when the presence is connected to paid social and a clear way to get in touch. Followers are a means; qualified enquiries are the objective.' },
    ],
    related: ['meta-ads', 'web-design-development', 'crm-automation'],
    cta: { label: 'Build My Social Presence', to: '/contact?intent=contractor_growth&service=social-media-marketing' },
  },
  {
    slug: 'lead-generation',
    stage: 'convert',
    stageLabel: 'Convert',
    name: 'Lead Generation',
    short: 'Full-funnel lead systems: traffic to landing page to qualified appointment.',
    metaTitle: 'Lead Generation Services for Local Businesses | HyenaMind',
    metaDescription:
      'A lead generation system you own: traffic, landing pages, capture, qualification, follow-up and tracking, judged on qualified enquiries rather than raw volume.',
    heroTitle: 'A lead system, not a lead trick.',
    heroText:
      'Traffic becomes a landing page, a landing page becomes an enquiry, and an enquiry becomes qualified, booked and followed up. We build that whole pipeline for local and service businesses, in channels you own, and judge it on the enquiries worth your time rather than on volume.',
    visual: 'lead-generation',
    problem: {
      title: 'Volume without quality is expensive noise',
      text: 'Anyone can generate form fills. What a business needs is enquiries from people with a real need, in an area you cover, who intend to proceed. That difference is why [shared contractor leads can cost more than they look](/resources/contractors/stop-buying-shared-contractor-leads), and the same logic applies to any bought lead.',
      points: ['Leads that never answer the phone', 'No qualification before your time is spent', 'No visibility from first click to finished job'],
    },
    deliverables: ['Traffic strategy across channels', 'Conversion-focused landing pages', 'Lead capture and forms', 'Lead qualification', 'Appointment setting and follow-up', 'Call and conversion tracking', 'Marketing automation', 'Offline conversion import from your CRM'],
    fit: {
      text: 'Businesses that want enquiries they control rather than leads they rent.',
      points: [
        'You buy shared leads and compete for every one of them',
        'Enquiries arrive but too few are worth quoting',
        'Nobody can say which channel produces the jobs',
        'Follow-up depends on who remembers',
      ],
    },
    process: [
      { title: 'Traffic', text: 'Search, social and local channels aimed at people who need your service, in the areas you cover.' },
      { title: 'Landing page', text: 'Pages matched to each offer and intent, built to turn the visit into an enquiry.' },
      { title: 'Capture', text: 'The context that matters: what they need, where, when and roughly how big the job is.' },
      { title: 'Qualification', text: 'Checked for service, area and intent before your time is spent.' },
      { title: 'Follow-up and measurement', text: 'Booked where it applies, followed up automatically, and tracked through to the outcome.' },
    ],
    costFactors: [
      { t: 'Channels', d: 'Each channel adds set-up, creative and management. Most systems start with one or two.' },
      { t: 'Landing pages', d: 'One page per offer and intent; how many depends on your services and areas.' },
      { t: 'Qualification and follow-up', d: 'Automated follow-up, booking and CRM work vary with the tools you already use.' },
      { t: 'Media budget', d: 'Paid traffic is a separate cost set by the auction in your market, kept visible in your own ad accounts.' },
    ],
    measures: [
      { t: 'Cost per qualified enquiry', d: 'Volume is reported, but qualified enquiries are what the system is judged on.' },
      { t: 'Speed to first response', d: 'How quickly each enquiry is acknowledged, because the first useful reply often wins.' },
      { t: 'Enquiry-to-job rate by source', d: 'Which channels produce work, not only enquiries, using outcomes recorded in your CRM.' },
    ],
    faqs: [
      { q: 'What matters more: lead volume or lead quality?', a: 'Quality. A smaller number of enquiries from people with a real need, in your area, beats a large number of unfiltered form fills. The whole system is built around that principle.' },
      { q: 'How do you track leads from first click to finished job?', a: 'Campaign tagging, call tracking and conversion tracking record where each enquiry came from, and outcomes from your CRM are matched back to that source, so budget moves toward what produces jobs rather than clicks.' },
      { q: 'Is this the same as buying leads?', a: 'No. Bought leads are rented: the next one costs the same and the source belongs to someone else. A lead generation system builds pages, campaigns and data that stay yours. Contractors who need opportunities now can also use HyenaMind\'s qualified lead service alongside it.' },
    ],
    related: ['lead-qualification', 'conversion-optimization', 'tracking-analytics'],
    contractor: [
      { to: '/contractors/lead-generation', label: 'Lead generation for contractors' },
      { to: '/contractors/get-qualified-leads', label: 'Qualified leads for contractors' },
    ],
    cta: { label: 'Build My Lead System', to: '/contact?intent=contractor_growth&service=lead-generation' },
  },
  {
    slug: 'tracking-analytics',
    stage: 'grow',
    stageLabel: 'Grow',
    name: 'Conversion Tracking & Analytics',
    short: 'Know which channel, page and campaign produced each enquiry, and each job.',
    metaTitle: 'Conversion Tracking & Analytics (GA4, GTM) | HyenaMind',
    metaDescription:
      'GA4, Google Tag Manager, call and form tracking, Google Ads and Meta conversions, and CRM outcome imports, so marketing spend is judged on enquiries and jobs.',
    heroTitle: 'If it is not measured properly, it is being guessed.',
    heroText:
      'Most local businesses know what they spent on marketing and roughly how busy they were. Few can say which campaign produced which enquiry, or which of those became paid work. We set up the tracking that answers that and keep it honest: real conversions only, counted once, with no personal data sent where it does not belong.',
    visual: 'tracking',
    problem: {
      title: 'Bad tracking is worse than none, because it is believed',
      text: 'Duplicated conversions, page views reported as leads and calls with no source all make a campaign look better or worse than it is, and the budget follows the error. Measurement is fixed before spend is increased, because every later decision depends on it. It is also the step that lets you [stop renting leads](/resources/contractors/stop-buying-shared-contractor-leads) with confidence.',
      points: [
        'Conversions counted twice, or counted when nothing happened',
        'Phone calls with no source attached',
        'Ad platforms optimising toward page views instead of enquiries',
        'No link between an enquiry and whether it became work',
      ],
    },
    deliverables: [
      'GA4 property, key events and audiences',
      'Google Tag Manager container, documented',
      'Form and booking tracking',
      'Call tracking with source attribution',
      'Google Ads conversions and enhanced conversions',
      'Meta Pixel and Conversions API',
      'Campaign tagging (UTM) conventions',
      'Offline conversion import from your CRM',
      'Consent settings for the markets you serve',
    ],
    fit: {
      text: 'Businesses that spend on marketing and want to know what it returns.',
      points: [
        'You spend on ads and cannot say what a qualified enquiry costs',
        'Reports show clicks and impressions, not enquiries and jobs',
        'Phone calls are a large share of your enquiries',
        'You are about to launch a new website or campaign',
      ],
    },
    process: [
      { title: 'Audit what exists', text: 'Every tag, event and conversion currently firing, tested end to end, with duplicates and gaps listed.' },
      { title: 'Define a conversion', text: 'Agree what counts: a submitted enquiry, a connected call, a booking. Not page views or button clicks.' },
      { title: 'Implement through Tag Manager', text: 'One documented container, so changes are reviewable and nothing is hard-coded twice.' },
      { title: 'Connect outcomes', text: 'Enquiry outcomes from your CRM matched back to their source and, where it fits, imported into the ad platforms.' },
      { title: 'Verify and hand over', text: 'Test conversions confirmed in each platform, with a short record of what is tracked and why.' },
    ],
    costFactors: [
      { t: 'Platforms involved', d: 'GA4 alone is a smaller job than GA4 plus Google Ads, Meta and a CRM connection.' },
      { t: 'Call tracking', d: 'Tracking numbers carry a small ongoing cost that depends on call volume and the provider chosen.' },
      { t: 'Your CRM', d: 'Importing outcomes depends on what your CRM can export or connect to.' },
      { t: 'Consent requirements', d: 'Visitors from some regions need consent handling before tracking runs, which affects set-up.' },
    ],
    measures: [
      { t: 'Data you can trust', d: 'Each conversion tested end to end; duplicates and spam excluded before anything is reported.' },
      { t: 'Cost per qualified enquiry and per job', d: 'By channel and campaign, so budget decisions rest on outcomes.' },
      { t: 'Source on every record', d: 'The channel and campaign kept on each enquiry in your CRM, not only in analytics.' },
    ],
    faqs: [
      { q: 'What should count as a conversion?', a: 'An action that shows real interest: a submitted enquiry, a call that connected and lasted long enough to be a conversation, or a booked appointment. Page views and button clicks are useful signals, but they should not be reported as leads.' },
      { q: 'Can you track phone calls?', a: 'Yes. Call tracking shows visitors from each channel a source-specific number and records which campaign produced the call. Calls from search ads can also be counted as conversions inside Google Ads.' },
      { q: 'Is personal information sent to Google Analytics?', a: 'It must not be. Google\'s Analytics policies mandate that no data be passed to Google that it could recognise as personally identifiable information, such as email addresses or personal phone numbers. Enquiry details stay in your CRM; analytics receives the event, not the person.' },
      { q: 'My agency already reports results. Do I need this?', a: 'Ask whether the reported conversions are real enquiries, counted once, and whether they can be matched to jobs. If the answer is unclear, a tracking audit will show where the numbers come from.' },
    ],
    related: ['google-ads', 'meta-ads', 'crm-automation'],
    cta: { label: 'Audit My Tracking', to: '/contact?intent=contractor_growth&service=tracking-analytics' },
  },
];

export const getService = (slug) => SERVICES.find((s) => s.slug === slug);
